import React, { useState, useEffect } from 'react';
import { getStates, getDistricts, getCities, getPincodeForCity } from '../../services/indiaLocationService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Building2, Mail, Phone, MapPin, Upload, User, Calendar, Shield, Globe, Lock, Eye, EyeOff, FileText, CheckCircle, ArrowLeft, UserPlus, Plus, Trash2, BookOpen, Edit2, Video, AlertCircle } from 'lucide-react';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useDegrees } from '../../context/DegreeContext';
import { sanitizeInput, sanitizeObject } from '../../utils/sanitize';
import { validateEmail as validateEmailShared, validatePhone as validatePhoneShared, validatePAN, validateGST, validateWebsite, validatePincode } from '../../utils/validation';

const monthOptions = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 30 }, (_, i) => {
  const y = currentYear - 10 + i;
  return { value: String(y), label: String(y) };
});

const SearchableDropdown = ({ label, value, onChange, options, placeholder, required, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = React.useRef(null);

  useEffect(() => {
    setSearchQuery(value || '');
  }, [value, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => {
    const text = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
    return text.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="relative flex flex-col gap-1 w-full" ref={containerRef}>
      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          disabled={disabled}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          placeholder={placeholder}
          className={`w-full px-4 py-2.5 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-[#1a3884] transition-all pr-10 bg-white dark:bg-[#001736] ${
            disabled 
              ? 'bg-gray-50 dark:bg-gray-900/50 text-gray-400 border-gray-150 dark:border-gray-800 cursor-not-allowed' 
              : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white cursor-text hover:border-[#1a3884]'
          }`}
        />
        <span 
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer w-4 h-4 flex items-center justify-center transition-transform duration-200"
          style={{ transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)' }}
          onClick={() => {
            if (!disabled) setIsOpen(!isOpen);
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>

        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1.5 bg-white dark:bg-[#001E3D] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-56 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-2 duration-200">
            {filteredOptions.length === 0 ? (
              <div className="text-xs text-gray-400 dark:text-gray-500 px-4 py-3 text-center">
                No options found. You can type to add custom value.
              </div>
            ) : (
              filteredOptions.map((opt, i) => {
                const optVal = typeof opt === 'string' ? opt : opt.value;
                const optLabel = typeof opt === 'string' ? opt : opt.label;
                const isSelected = optVal === value;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      onChange(optVal);
                      setSearchQuery(optLabel);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-850'
                    }`}
                  >
                    {optLabel}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Numbered section header used by every Card on the onboarding form — gives the
 * long form a visible order and a one-line purpose per section.
 */
const SectionTitle = ({ step, title, subtitle }) => (
  <div className="flex items-center gap-3">
    {step != null && (
      <span className="w-7 h-7 rounded-lg bg-[#1a3884] text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-sm">
        {String(step).padStart(2, '0')}
      </span>
    )}
    <span className="min-w-0">
      <span className="block text-[15px] font-bold text-gray-900 dark:text-white leading-tight">{title}</span>
      {subtitle && (
        <span className="block text-xs font-normal text-gray-400 dark:text-slate-400 mt-0.5">{subtitle}</span>
      )}
    </span>
  </div>
);

const OnboardCollege = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const degreesOnly = searchParams.get('degreesOnly') === 'true';
  const [isTemporary, setIsTemporary] = useState(location.state?.isTemporary || false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const { degrees } = useDegrees(); // shared catalog — fetched once per session
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedDegreeGroup, setSelectedDegreeGroup] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [chains, setChains] = useState([]);
  const defaultBatch = {
    startYear: '',
    endYear: '',
    batch: '',
    referenceName: '',
    subscriptionPlan: {
      plan: 'Smaart Core',
      addons: { aiq: true, sq: false, piq: false, britishCouncil: false, collegePlacement: false }
    },
    validityDuration: '1 Year',
    validityTimeline: '',
    validityStartMonth: '06',
    validityStartYear: '',
    validityEndMonth: '06',
    validityEndYear: '',
    status: 'Active',
    expanded: true
  };
  const [modalBatches, setModalBatches] = useState([{ ...defaultBatch }]);
  const [editingOriginalIndices, setEditingOriginalIndices] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Initial State - Grouped logically but flattened for easy access
  const [formData, setFormData] = useState({
    // Section 1: Institution Identity
    institutionName: '', // Maps to collegeName
    departments: [],
    subscriptionPlan: {
      plan: 'Smaart Core',
      addons: {
        aiq: true,
        piq: false,
        sq: false,
        britishCouncil: false,
        collegePlacement: false
      }
    },
    institutionCode: 'Auto-generated',
    institutionType: '', // Default
    governanceType: '',
    logo: '',
    institutionEmail: '', // Maps to email
    institutionWebsite: '',
    contactNumber: '',
    country: 'India',
    state: '',
    city: '',
    district: '',
    dateOfOnboarding: new Date().toISOString().split('T')[0],

    // Section 2: Relationship
    affiliatedUniversity: '',
    universityType: '',
    instituteChain: '',

    // Section 3: Address
    addressLine: '', // Maps to address.street
    pincode: '',

    // Section 4: Primary Coordinator
    coordinatorName: '',
    coordinatorDesignation: '',
    coordinatorEmail: '',
    coordinatorMobile: '',

    // Section 5: Platform Admin Creation
    adminName: '',
    adminEmail: '',
    adminMobile: '',
    adminRole: 'Institute Admin', // Static
    adminPassword: '',
    adminConfirmPassword: '',

    // Section 6: Compliance
    registrationNumber: '',
    accreditationStatus: '',
    gstNumber: '',
    panNumber: '',

    // Section 7: Documents
    mouDocument: '',
    universityActProof: '',
    ndaDocument: '',
    chairmanVideo: '', // Chairman introduction video (MP4)

    // Section 8: Platform Configuration
    maxFacultyAccounts: 100,
    maxStudentAccounts: 1000,

    // Section 9: Status & Validity
    validityStartDate: new Date().toISOString().split('T')[0],
    validityEndDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    validityDuration: 1,
    status: 'Active'
  });

  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);

  // Calculate form progress (9 sections total)
  const calculateProgress = () => {
    let completedSections = 0;
    const totalSections = 6; // Address combined, Section 8, 9 removed

    // For new onboarding, start at 0% until basic info is entered
    if (!id && !formData.institutionName && !formData.institutionEmail && !formData.contactNumber) {
      return 0;
    }

    // Section 1: Institution Identity & Address
    if (formData.institutionName && formData.institutionType && formData.institutionEmail &&
      formData.institutionWebsite && formData.contactNumber && formData.state && formData.city &&
      formData.addressLine && formData.pincode) completedSections++;

    // Section 2: Relationship (conditional)
    if (formData.institutionType) {
      const isCollege = formData.institutionType.toLowerCase().includes('college') || formData.institutionType === 'College';
      if (!isCollege || formData.affiliatedUniversity) completedSections++;
    }

    // Section 3: Address (included in section 1 check above)

    // Section 4: Coordinator
    if (formData.coordinatorName && formData.coordinatorDesignation && formData.coordinatorEmail &&
      formData.coordinatorMobile) completedSections++;

    // Section 5: Admin (only for new onboarding)
    if (id || (formData.adminName && formData.adminEmail && formData.adminMobile &&
      formData.adminPassword && formData.adminPassword === formData.adminConfirmPassword)) completedSections++;

    // Section 6: Compliance
    if (formData.registrationNumber && formData.accreditationStatus) completedSections++;

    // Section 7: Documents
    if (formData.mouDocument && formData.universityActProof && formData.ndaDocument) completedSections++;

    return Math.round((completedSections / totalSections) * 100);
  };

  // Dropdown Options
  const institutionTypeOptions = [
    { value: 'Central University', label: 'Central University' },
    { value: 'State University', label: 'State University' },
    { value: 'Private University', label: 'Private University' },
    { value: 'Deemed University', label: 'Deemed University' },
    { value: 'Autonomous College', label: 'Autonomous College' },
    { value: 'Affiliated (Non-Autonomous) College', label: 'Affiliated (Non-Autonomous) College' },
    { value: 'Constituent College', label: 'Constituent College' },
    { value: 'Government-Aided College', label: 'Government-Aided College' },
    { value: 'Other', label: 'Other' }
  ];

  const governanceOptions = [
    { value: 'Private', label: 'Private' },
    { value: 'Government', label: 'Government' },
    { value: 'Autonomous', label: 'Autonomous' },
    { value: 'Deemed', label: 'Deemed' }
  ];

  const countryOptions = [
    { value: 'India', label: 'India' }
  ];

  // Full India list from the shared location dataset — the previous hardcoded
  // array held only 29 states and left out every union territory.
  const stateOptions = getStates().map(st => ({ value: st, label: st }));

  const districtOptions = getDistricts(formData.state).map(d => ({ value: d, label: d }));

  const filteredCities = (() => {
    const allCities = Array.from(new Set(
      getCities(formData.state, formData.district).map(c => c.city)
    )).sort();
    if (!citySearch) return allCities;
    return allCities.filter(c => c && c.toLowerCase().includes(citySearch.toLowerCase()));
  })();

  const universityTypeOptions = [
    { value: 'Central University', label: 'Central University' },
    { value: 'State University', label: 'State University' },
    { value: 'Private University', label: 'Private University' },
    { value: 'Deemed University', label: 'Deemed University' },
    { value: 'Autonomous College', label: 'Autonomous College' },
    { value: 'Affiliated (Non-Autonomous) College', label: 'Affiliated (Non-Autonomous) College' },
    { value: 'Constituent College', label: 'Constituent College' },
    { value: 'Government-Aided College', label: 'Government-Aided College' },
    { value: 'Other', label: 'Other' }
  ];

  const accreditationOptions = [
    { value: 'NAAC', label: 'NAAC' },
    { value: 'NBA', label: 'NBA' },
    { value: 'Both NAAC & NBA', label: 'Both NAAC & NBA' },
    { value: 'Other Accreditation / Approval', label: 'Other Accreditation / Approval' },
    { value: 'Applied / Under Review', label: 'Applied / Under Review' },
    { value: 'Not Accredited', label: 'Not Accredited' },
    { value: 'Not Applicable', label: 'Not Applicable' }
  ];

  const statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' }
  ];

  const validityDurationOptions = [
    { value: '1', label: '1 Year' },
    { value: '2', label: '2 Years' },
    { value: '3', label: '3 Years' },
    { value: '4', label: '4 Years' },
    { value: '5', label: '5 Years' }
  ];

  // Fetch existing data if in Edit mode
  useEffect(() => {
    if (id) {
      fetchCollege();
    } else {
      // Set initial form data for new onboarding
      setInitialFormData(formData);
    }
  }, [id]);

  // Fetch institute chains on mount
  useEffect(() => {
    const fetchChains = async () => {
      try {
        const { data } = await api.get('/institute-chains?limit=1000');
        if (data.success) {
          setChains(data.data);
        }
      } catch (err) {
        console.error('Failed to load institute chains:', err);
      }
    };
    fetchChains();
  }, []);



  // Track form changes for dirty state
  useEffect(() => {
    if (initialFormData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setIsDirty(hasChanges);
    }
  }, [formData, initialFormData]);

  // Add beforeunload event listener for dirty state warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const fetchCollege = async () => {
    try {
      const { data } = await api.get(`/colleges/${id}`);
      if (data.success) {
        const college = data.data;
        // Map backend data to frontend state
        const mappedData = {
          institutionName: sanitizeInput(college.collegeName || ''),
          institutionCode: sanitizeInput(college.collegeCode || ''),
          institutionType: sanitizeInput(college.institutionType || ''),
          governanceType: sanitizeInput(college.governanceType || ''),
          logo: college.logo || '',
          institutionEmail: sanitizeInput(college.email || ''),
          institutionWebsite: college.website || '',
          contactNumber: sanitizeInput(college.contactNumber || ''),
          country: sanitizeInput(college.address?.country || 'India'),
          state: sanitizeInput(college.address?.state || ''),
          city: sanitizeInput(college.address?.city || ''),
          district: sanitizeInput(college.address?.district || ''),
          dateOfOnboarding: (college.onboardingDate || college.onboardDate || college.createdAt)?.split('T')[0] || '',
          affiliatedUniversity: sanitizeInput(college.affiliatedUniversity || college.affiliation || ''),
          universityType: sanitizeInput(college.universityType || ''),
          instituteChain: college.instituteChain?._id || college.instituteChain || '',
          addressLine: sanitizeInput(college.address?.street || ''),
          pincode: sanitizeInput(college.address?.pincode || ''),

          coordinatorName: sanitizeInput(college.coordinatorInfo?.name || ''),
          coordinatorDesignation: sanitizeInput(college.coordinatorInfo?.designation || ''),
          coordinatorEmail: sanitizeInput(college.coordinatorInfo?.email || ''),
          coordinatorMobile: sanitizeInput(college.coordinatorInfo?.mobile || ''),

          // Admin details from database
          adminName: sanitizeInput(college.collegeAdmin?.fullName || college.collegeAdmin?.name || ''),
          adminEmail: sanitizeInput(college.collegeAdmin?.email || ''),
          adminMobile: sanitizeInput(college.collegeAdmin?.mobile || ''),
          adminRole: 'Institute Admin',
          adminPassword: '',
          adminConfirmPassword: '',

          registrationNumber: sanitizeInput(college.registrationNumber || ''),
          accreditationStatus: sanitizeInput(college.accreditationStatus || ''),
          gstNumber: sanitizeInput(college.gstNumber || ''),
          panNumber: sanitizeInput(college.panNumber || ''),

          mouDocument: college.mouDocument || '',
          universityActProof: college.universityActProof || '',
          ndaDocument: college.ndaDocument || '',
          chairmanVideo: college.chairmanVideo || '',

          maxFacultyAccounts: college.maxFaculty || 100,
          maxStudentAccounts: college.maxStudents || 1000,

          validityStartDate: college.validityStartDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          validityEndDate: college.validityEndDate?.split('T')[0] || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          validityDuration: college.validityDuration || 1,
          status: sanitizeInput(college.status || 'Draft'),
          departments: (college.departments || []).map(dept => {
            return {
              degreeId: dept.degreeId || '',
              uniqueId: sanitizeInput(dept.uniqueId || ''),
              level: sanitizeInput(dept.level || ''),
              domain: sanitizeInput(dept.domain || ''),
              fullName: sanitizeInput(dept.fullName || ''),
              abbreviation: sanitizeInput(dept.abbreviation || ''),
              specialization: Array.isArray(dept.specialization) ? dept.specialization : sanitizeInput(dept.specialization || ''),
              duration: sanitizeInput(dept.duration || ''),
              status: sanitizeInput(dept.status || 'Active'),
              batches: (dept.batches || []).map(b => ({
                batch: sanitizeInput(b.batch || ''),
                startYear: sanitizeInput(b.startYear || ''),
                endYear: sanitizeInput(b.endYear || ''),
                validityDuration: sanitizeInput(b.validityDuration || '1'),
                subscriptionPlan: {
                  plan: b.subscriptionPlan?.plan || 'Smaart Core',
                  addons: {
                    aiq: true,
                    piq: b.subscriptionPlan?.addons?.piq ?? (b.subscriptionPlan?.plan === 'Smaart Complete'),
                    sq: b.subscriptionPlan?.addons?.sq ?? (b.subscriptionPlan?.plan === 'Smaart Standard' || b.subscriptionPlan?.plan === 'Smaart Complete'),
                    britishCouncil: b.subscriptionPlan?.addons?.britishCouncil ?? false,
                    collegePlacement: b.subscriptionPlan?.addons?.collegePlacement ?? false
                  }
                },
                status: sanitizeInput(b.status || 'Active')
              }))
            };
          }),
          subscriptionPlan: {
            plan: college.subscriptionPlan?.plan || college.plan || 'Smaart Core',
            addons: {
              aiq: true,
              piq: college.subscriptionPlan?.addons?.piq ?? college.addons?.piq ?? false,
              sq: college.subscriptionPlan?.addons?.sq ?? college.addons?.sq ?? false,
              britishCouncil: college.subscriptionPlan?.addons?.britishCouncil ?? college.addons?.britishCouncil ?? false,
              collegePlacement: college.subscriptionPlan?.addons?.collegePlacement ?? college.addons?.collegePlacement ?? false
            }
          }
        };
        setFormData(mappedData);
        setInitialFormData(mappedData);
      }
    } catch (error) {
      console.error('Failed to fetch institute details:', error);
      if (error.response?.status === 404) {
        toast.error('Institute not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this institute');
      } else if (error.response?.status === 500) {
        toast.error('Server error while loading institute details. Please try again later');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network');
      } else {
        toast.error('Failed to fetch institute details');
      }
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    return validateEmailShared(email);
  };

  const validatePhone = (phone) => {
    return validatePhoneShared(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: '' }));

    // Filter non-numeric characters for phone numbers
    if (name === 'contactNumber' || name === 'coordinatorMobile' || name === 'adminMobile') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    // Filter non-numeric characters for pincode
    if (name === 'pincode') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 6);
    }

    // Force uppercase for PAN and GST
    if (name === 'panNumber' || name === 'gstNumber') {
      sanitizedValue = value.toUpperCase();
    }

    // Real-time validation
    if (name === 'institutionEmail' || name === 'coordinatorEmail' || name === 'adminEmail') {
      if (sanitizedValue && !validateEmail(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Invalid email format' }));
      }
    }

    if (name === 'contactNumber' || name === 'coordinatorMobile' || name === 'adminMobile') {
      if (sanitizedValue && !validatePhone(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Must be exactly 10 digits' }));
      }
    }

    if (name === 'panNumber') {
      if (sanitizedValue && !validatePAN(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Invalid PAN format' }));
      }
    }

    if (name === 'gstNumber') {
      if (sanitizedValue && !validateGST(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Invalid GST format' }));
      }
    }

    if (name === 'institutionWebsite') {
      if (sanitizedValue && !validateWebsite(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Invalid website URL format' }));
      }
    }

    if (name === 'pincode') {
      if (sanitizedValue && !validatePincode(sanitizedValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Must be exactly 6 digits' }));
      }
    }

    // Changing state invalidates district, city and pincode beneath it
    if (name === 'state') {
      setFormData(prev => ({ ...prev, state: sanitizedValue, district: '', city: '', pincode: '' }));
      setCitySearch('');
      setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    // Auto-calculate validity end date when start date or duration changes
    if (name === 'validityStartDate' && value) {
      const startDate = new Date(value);
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + (formData.validityDuration || 1)); // Add selected duration in years

      setFormData(prev => ({
        ...prev,
        [name]: value,
        validityEndDate: endDate.toISOString().split('T')[0]
      }));
    } else if (name === 'validityDuration' && value) {
      const duration = parseInt(value);
      const startDate = new Date(formData.validityStartDate);
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + duration); // Add selected duration in years

      setFormData(prev => ({
        ...prev,
        [name]: value,
        validityEndDate: endDate.toISOString().split('T')[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation
    if (fieldName === 'logo') {
      if (!file.type.startsWith('image/')) {
        toast.error('Logo must be an image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB Limit matched with backend
        toast.error('Image size must be less than 5MB');
        return;
      }
    } else if (fieldName === 'chairmanVideo') {
      if (file.type !== 'video/mp4') {
        toast.error('Chairman video must be an MP4 file');
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video size must be less than 100MB');
        return;
      }
    } else {
      // Document fields (allow image and pdf both)
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast.error('File must be an image or a PDF document');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit for documents
        toast.error('File size must be less than 10MB');
        return;
      }
    }

    setUploading(true);
    const uploadData = new FormData();

    // Determine endpoint and field name based on file type/field
    let endpoint = '/upload/document';
    let formDataKey = 'document';

    if (fieldName === 'logo') {
      endpoint = '/upload/image';
      formDataKey = 'image';
    } else if (fieldName === 'chairmanVideo') {
      endpoint = '/upload/video';
      formDataKey = 'video';
    }

    uploadData.append(formDataKey, file);

    try {
      const { data } = await api.post(endpoint, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        setFormData(prev => ({ ...prev, [fieldName]: data.data.url }));
        toast.success(`${fieldName === 'logo' ? 'Logo' : fieldName === 'chairmanVideo' ? 'Video' : 'Document'} uploaded successfully`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 404) {
        toast.error('Upload endpoint not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to upload files');
      } else if (error.response?.status === 500) {
        toast.error('Server error while uploading. Please try again later');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network');
      } else {
        toast.error(error.response?.data?.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const getPasswordStrength = (pass) => {
    if (!pass) return 0;
    let score = 0;
    if (pass.length >= 8) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^a-zA-Z0-9]/.test(pass)) score++;
    return score;
  };

  const validateForm = () => {
    // Section 1 Mandatory
    if (!formData.institutionName) return 'Institution Name is required';
    if (!formData.institutionType) return 'Institution Type is required';
    if (!formData.institutionType?.includes('University') && !formData.governanceType) return 'Governance Type is required';
    if (!formData.logo) return 'Institution Logo is required';
    if (!formData.institutionEmail) return 'Institution Email is required';
    if (!validateEmail(formData.institutionEmail)) return 'Institution Email format is invalid';
    if (!formData.institutionWebsite) return 'Website is required';
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!urlRegex.test(formData.institutionWebsite)) return 'Website must be a valid URL';
    if (!formData.contactNumber) return 'Contact Number is required';
    if (formData.contactNumber.length !== 10) return 'Contact Number must be 10 digits';
    if (!formData.state) return 'State is required';
    if (!formData.district) return 'District is required';
    if (!formData.city) return 'City is required';

    // Section 2 Conditional
    const isInstCollege = formData.institutionType?.toLowerCase().includes('college') || formData.institutionType === 'College';

    if (isInstCollege && !formData.affiliatedUniversity) {
      return 'Affiliated University is required for Institutes';
    }

    // Section 3
    if (!formData.addressLine) return 'Address Line is required';
    if (!formData.pincode || formData.pincode.length !== 6) return 'Valid 6-digit Pincode is required';

    // Skip the rest if Temporary
    if (isTemporary) return null;

    // Section 4
    if (!formData.coordinatorName) return 'Coordinator Name is required';
    if (!formData.coordinatorDesignation) return 'Coordinator Designation is required';
    if (!formData.coordinatorEmail) return 'Coordinator Email is required';
    if (!validateEmail(formData.coordinatorEmail)) return 'Coordinator Email format is invalid';
    if (!formData.coordinatorMobile || formData.coordinatorMobile.length !== 10) return 'Coordinator Mobile must be 10 digits';

    // Section 5 (Admin)
    if (!formData.adminName) return 'Admin Name is required';
    if (!formData.adminEmail) return 'Admin Email is required';
    if (!validateEmail(formData.adminEmail)) return 'Admin Email format is invalid';
    if (!formData.adminMobile) return 'Admin Mobile is required';

    if (!id) {
      if (!formData.adminPassword) return 'Initial Password is required';
      const strength = getPasswordStrength(formData.adminPassword);
      if (strength < 3) return 'Password is too weak. Must contain Uppercase, Number, and Symbol.';
      if (formData.adminPassword !== formData.adminConfirmPassword) return 'Passwords do not match';
    } else if (formData.adminPassword) {
      const strength = getPasswordStrength(formData.adminPassword);
      if (strength < 3) return 'Password is too weak. Must contain Uppercase, Number, and Symbol.';
      if (formData.adminPassword !== formData.adminConfirmPassword) return 'Passwords do not match';
    }

    // Section 6
    if (!formData.registrationNumber) return 'Registration Number is required';
    if (!formData.accreditationStatus) return 'Accreditation Status is required';

    if (formData.panNumber) {
      if (formData.panNumber.length !== 10) return 'PAN must be exactly 10 characters';
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) return 'Invalid PAN format';
    }

    if (formData.gstNumber) {
      if (formData.gstNumber.length !== 15) return 'GST must be exactly 15 characters';
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) return 'Invalid GST format';
    }

    // Section 7
    if (!formData.mouDocument) return 'MoU / Authorization Letter is required';
    if (!formData.universityActProof) return 'Registration / University Act Proof is required';



    // Section 9
    if (!formData.validityStartDate) return 'Validity Start Date is required';
    if (formData.validityEndDate && formData.validityEndDate < formData.validityStartDate) {
      return 'Validity End Date must be after the Start Date';
    }
    if (!formData.status) return 'Institution Status is required';

    return null;
  };

  const handlePlanChange = (selectedPlan) => {
    setFormData(prev => {
      const newAddons = { ...(prev.subscriptionPlan?.addons || {}) };
      newAddons.aiq = true; // AIQ is always included across all plans

      if (selectedPlan === 'Smaart Standard') {
        newAddons.sq = true;
      } else if (selectedPlan === 'Smaart Complete') {
        newAddons.sq = true;
        newAddons.piq = true;
      }
      return {
        ...prev,
        subscriptionPlan: {
          plan: selectedPlan,
          addons: newAddons
        }
      };
    });
  };

  const handleAddonChange = (addonName, checked) => {
    setFormData(prev => ({
      ...prev,
      subscriptionPlan: {
        ...prev.subscriptionPlan,
        addons: {
          ...(prev.subscriptionPlan?.addons || {}),
          [addonName]: checked
        }
      }
    }));
  };

  const updateBatch = (index, field, value) => {
    const updatedBatches = [...modalBatches];
    updatedBatches[index] = { ...updatedBatches[index], [field]: value };

    // Automatically sync startYear / endYear validity selections if empty
    if (field === 'startYear' && value) {
      if (!updatedBatches[index].validityStartYear) {
        updatedBatches[index].validityStartYear = value;
      }
    }
    if (field === 'endYear' && value) {
      if (!updatedBatches[index].validityEndYear) {
        updatedBatches[index].validityEndYear = value;
      }
    }

    // Recompute validityTimeline based on validity start/end month and year
    const startM = updatedBatches[index].validityStartMonth || '06';
    const startY = updatedBatches[index].validityStartYear || updatedBatches[index].startYear || '';
    const endM = updatedBatches[index].validityEndMonth || '06';
    const endY = updatedBatches[index].validityEndYear || updatedBatches[index].endYear || '';

    const startMonthLabel = monthOptions.find(m => m.value === startM)?.label || 'June';
    const endMonthLabel = monthOptions.find(m => m.value === endM)?.label || 'June';

    if (startY && endY) {
      updatedBatches[index].validityTimeline = `${startMonthLabel} ${startY} - ${endMonthLabel} ${endY}`;
    } else if (startY) {
      updatedBatches[index].validityTimeline = `${startMonthLabel} ${startY} - ...`;
    } else {
      updatedBatches[index].validityTimeline = '';
    }

    if (field === 'startYear' || field === 'endYear') {
      const start = parseInt(updatedBatches[index].startYear);
      const end = parseInt(updatedBatches[index].endYear);

      if (!isNaN(start) && !isNaN(end) && start <= end) {
        updatedBatches[index].batch = `${start}-${end}`;

        const currentYear = new Date().getFullYear();
        const totalYears = Math.max(1, end - start);
        const yearDiff = currentYear - start + 1;

        const getOrdinal = (n) => {
          const s = ["th", "st", "nd", "rd"];
          const v = n % 100;
          return n + (s[(v - 20) % 10] || s[v] || s[0]) + " Year";
        };

        if (yearDiff < 1) {
          updatedBatches[index].validityDuration = "Not Started";
        } else if (yearDiff > totalYears) {
          updatedBatches[index].validityDuration = "Completed";
        } else {
          updatedBatches[index].validityDuration = getOrdinal(yearDiff);
        }
      } else if (!isNaN(start)) {
        updatedBatches[index].batch = `${start}-...`;
      } else {
        updatedBatches[index].batch = '';
      }
    }
    setModalBatches(updatedBatches);
  };

  const updateBatchPlan = (index, planId) => {
    const updatedBatches = [...modalBatches];
    const prevAddons = updatedBatches[index]?.subscriptionPlan?.addons || {};
    const newAddons = {
      aiq: true,
      sq: (planId === 'Smaart Standard' || planId === 'Smaart Complete') ? true : (prevAddons.sq || false),
      piq: planId === 'Smaart Complete' ? true : (prevAddons.piq || false),
      britishCouncil: !!prevAddons.britishCouncil,
      collegePlacement: !!prevAddons.collegePlacement
    };

    updatedBatches[index] = {
      ...updatedBatches[index],
      subscriptionPlan: {
        plan: planId,
        addons: newAddons
      }
    };
    setModalBatches(updatedBatches);
  };

  const updateBatchAddon = (index, addonName, checked) => {
    const updatedBatches = [...modalBatches];
    const currentSub = updatedBatches[index]?.subscriptionPlan || { plan: 'Smaart Core', addons: { aiq: true } };
    updatedBatches[index] = {
      ...updatedBatches[index],
      subscriptionPlan: {
        ...currentSub,
        addons: {
          ...(currentSub.addons || {}),
          aiq: true,
          [addonName]: checked
        }
      }
    };
    setModalBatches(updatedBatches);
  };

  const getDynamicDurationOptions = (batch) => {
    const start = parseInt(batch.startYear);
    const end = parseInt(batch.endYear);
    const options = [];
    if (!isNaN(start) && !isNaN(end) && start <= end) {
      const totalYears = Math.max(1, end - start);
      const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]) + " Year";
      };
      for (let i = 1; i <= totalYears; i++) {
        options.push({ value: getOrdinal(i), label: getOrdinal(i) });
      }
      options.push({ value: "Not Started", label: "Not Started" });
      options.push({ value: "Completed", label: "Completed" });
    } else {
      options.push({ value: '1 Year', label: '1 Year' });
      options.push({ value: '2 Years', label: '2 Years' });
      options.push({ value: '3 Years', label: '3 Years' });
      options.push({ value: '4 Years', label: '4 Years' });
      options.push({ value: '5 Years', label: '5 Years' });
    }
    if (batch.validityDuration && !options.find(o => o.value === batch.validityDuration)) {
      options.push({ value: batch.validityDuration, label: batch.validityDuration });
    }
    return options;
  };

  // ---- Degree catalog -----------------------------------------------------
  // UG is driven entirely by the curated catalog uploaded from the UG degrees
  // sheet — every one of those 274 records carries a `courseId`. The other
  // levels still read the full master list, which remains the only source for
  // PG / Diploma / Doctoral.
  const isUgLevel = selectedLevel === 'Undergraduate (UG)';

  const levelPool = !selectedLevel
    ? []
    : isUgLevel
      ? degrees.filter(d => d.courseId && d.sourceDegree)
      : degrees.filter(d => d.level === selectedLevel);

  // The uploaded sheet has no domain column, so each catalog record inherited
  // the domain of the master record it merged onto — one domain per degree.
  // That makes domain a poor first step for UG: "Computer Science & IT" holds
  // only BCA, while B.Sc. Computer Science sits under "Science" and B.E. CSE
  // under "Engineering & Technology". So for UG the Degree list is not gated on
  // domain — domain becomes an optional narrowing filter that is auto-filled
  // from whichever degree is chosen. PG / Diploma / Doctoral keep the original
  // Level -> Domain -> Degree flow, where the domains are properly populated.
  // Deliberately ignores selectedDomain on UG: the domain is auto-filled from
  // whichever degree gets picked, and filtering by it would then collapse the
  // Degree list down to that one degree.
  const degreeScope = isUgLevel ? levelPool : levelPool.filter(d => d.domain === selectedDomain);

  const specScope = degreeScope;

  // Show a degree the way the source sheet writes it (B.E., BBA, MBBS) rather
  // than the canonical stored abbreviation (B.E., B.B.A., M.B.B.S.).
  const degreeLabelOf = (d) =>
    (d.courseId && d.sourceDegree) ? d.sourceDegree : (d.fullName || d.abbreviation || '');

  // One degree can be referred to by its sheet label (BBA), its canonical
  // abbreviation (B.B.A.) or its full name. Resolve any of them into the same
  // alias set so saving, editing and duplicate detection agree on identity.
  const degreeAliases = (label) => {
    const key = String(label || '').trim().toLowerCase();
    const aliases = new Set(key ? [key] : []);
    if (!key) return aliases;
    for (const d of degrees) {
      const names = [d.sourceDegree, d.abbreviation, d.fullName]
        .filter(Boolean)
        .map(n => String(n).trim().toLowerCase());
      if (names.includes(key)) names.forEach(n => aliases.add(n));
    }
    return aliases;
  };

  const degreeMatches = (d, label) => {
    const aliases = degreeAliases(label);
    return [d.sourceDegree, d.abbreviation, d.fullName]
      .filter(Boolean)
      .some(n => aliases.has(String(n).trim().toLowerCase()));
  };

  // A saved registration stores the canonical fullName. Translate it back to
  // the label the dropdown now shows so editing doesn't open on a blank field.
  const degreeLabelFromSaved = (group) => {
    const saved = String(group.fullName || group.abbreviation || '').trim();
    if (!saved) return '';
    const aliases = degreeAliases(saved);
    const match = degrees.find(d =>
      [d.sourceDegree, d.abbreviation, d.fullName]
        .filter(Boolean)
        .some(n => aliases.has(String(n).trim().toLowerCase()))
    );
    return match ? degreeLabelOf(match) : saved;
  };

  // Keep a value already saved against the college visible in its dropdown even
  // when it predates the catalog, otherwise editing an older registration
  // silently drops what was there.
  const withCurrent = (list, current) => {
    const extras = (Array.isArray(current) ? current : [current])
      .map(v => String(v || '').trim())
      .filter(v => v && !list.includes(v));
    return extras.length ? [...list, ...extras].sort() : list;
  };

  const handleAddDept = (e) => {
    e.preventDefault();
    // For UG the domain is derived from the degree rather than chosen up front,
    // so only Level and Degree are asked for.
    if (!selectedLevel || !selectedDegreeGroup || (!isUgLevel && !selectedDomain)) {
      toast.error(isUgLevel ? 'Please select Level and Degree' : 'Please select Level, Domain, and Degree');
      return;
    }

    const isPG = selectedLevel === 'Postgraduate (PG)';
    let specsToRegister = [];

    if (isPG) {
      if (selectedSpecializations.length === 0) {
        specsToRegister = ['General'];
      } else {
        specsToRegister = [...selectedSpecializations];
      }
    } else {
      specsToRegister = [selectedSpecialization || 'General'];
    }

    for (let i = 0; i < modalBatches.length; i++) {
      const b = modalBatches[i];
      if (!b.startYear || !b.endYear) {
        toast.error(`Please provide Start and End year for Batch #${i + 1}`);
        return;
      }
      if (b.startYear >= b.endYear) {
        toast.error(`Start Month & Year must be before End Month & Year for Batch #${i + 1}`);
        return;
      }
    }

    const newBatches = modalBatches.map(b => ({
      batch: b.batch,
      startYear: b.startYear,
      endYear: b.endYear,
      validityDuration: b.validityDuration ? String(b.validityDuration).replace(/\D/g, '') || '1' : '1',
      subscriptionPlan: {
        plan: b.subscriptionPlan?.plan || 'Smaart Core',
        addons: {
          aiq: true,
          piq: b.subscriptionPlan?.plan === 'Smaart Complete' ? true : (b.subscriptionPlan?.addons?.piq || false),
          sq: (b.subscriptionPlan?.plan === 'Smaart Standard' || b.subscriptionPlan?.plan === 'Smaart Complete') ? true : (b.subscriptionPlan?.addons?.sq || false),
          britishCouncil: b.subscriptionPlan?.addons?.britishCouncil || false,
          collegePlacement: b.subscriptionPlan?.addons?.collegePlacement || false
        }
      },
      status: b.status || 'Active'
    }));

    const specValue = isPG ? (selectedSpecializations.length > 0 ? selectedSpecializations : ['General']) : (selectedSpecialization || 'General');

    if (editingOriginalIndices !== null && editingOriginalIndices.length > 0) {
      const firstSpec = Array.isArray(specValue) ? specValue[0] : specValue;

      // `let`, not `const` — the fallback below reassigns this when the degree
      // was typed in freehand rather than picked from the catalog.
      let matchedDegree = specScope.find(d =>
        degreeMatches(d, selectedDegreeGroup) &&
        d.specialization === firstSpec
      ) || specScope.find(d =>
        degreeMatches(d, selectedDegreeGroup)
      );

      if (!matchedDegree) {
        matchedDegree = {
          _id: null,
          uniqueId: `CUSTOM-${Date.now()}`,
          level: selectedLevel,
          // UG never asks for a domain, so a freehand degree has none to carry
          // over — fall back rather than send an empty required field.
          domain: selectedDomain || 'Other',
          fullName: selectedDegreeGroup,
          abbreviation: selectedDegreeGroup,
          duration: 'N/A'
        };
      }

      const newDept = {
        degreeId: matchedDegree._id,
        uniqueId: matchedDegree.uniqueId,
        level: matchedDegree.level,
        domain: matchedDegree.domain,
        fullName: matchedDegree.fullName,
        abbreviation: matchedDegree.abbreviation,
        specialization: specValue,
        duration: matchedDegree.duration || 'N/A',
        batches: newBatches,
        status: 'Active'
      };

      setFormData(prev => {
        let updated = [...(prev.departments || [])];
        const targetIdx = editingOriginalIndices[0];
        updated[targetIdx] = newDept;
        return { ...prev, departments: updated };
      });
      toast.success('Degree and batches updated successfully');
    } else {
      const firstSpec = Array.isArray(specValue) ? specValue[0] : specValue;
      // `let`, not `const` — the fallback below reassigns this when the degree
      // was typed in freehand rather than picked from the catalog.
      let matchedDegree = specScope.find(d =>
        degreeMatches(d, selectedDegreeGroup) &&
        d.specialization === firstSpec
      ) || specScope.find(d =>
        degreeMatches(d, selectedDegreeGroup)
      );

      if (!matchedDegree) {
        matchedDegree = {
          _id: null,
          uniqueId: `CUSTOM-${Date.now()}`,
          level: selectedLevel,
          // UG never asks for a domain, so a freehand degree has none to carry
          // over — fall back rather than send an empty required field.
          domain: selectedDomain || 'Other',
          fullName: selectedDegreeGroup,
          abbreviation: selectedDegreeGroup,
          duration: 'N/A'
        };
      }

      // Compare through the alias set: an entry saved as "Bachelor of Business
      // Administration" is the same degree as one picked as "BBA", and a plain
      // string compare would miss the duplicate.
      const selectedAliases = degreeAliases(selectedDegreeGroup);
      const duplicate = (formData.departments || []).find(d => {
        const dNames = [d.fullName, d.abbreviation, d.degree, d.degreeGroup]
          .filter(Boolean)
          .map(n => String(n).toLowerCase().trim());
        if (!dNames.some(n => selectedAliases.has(n))) return false;
        const dSpecs = Array.isArray(d.specialization) ? d.specialization : [d.specialization || 'General'];
        const newSpecs = Array.isArray(specValue) ? specValue : [specValue];
        return newSpecs.some(s => dSpecs.some(ds => (ds || '').toLowerCase().trim() === (s || '').toLowerCase().trim()));
      });

      if (duplicate) {
        toast.error('Degree with specialization already added.');
        return;
      }

      const newDept = {
        degreeId: matchedDegree._id,
        uniqueId: matchedDegree.uniqueId,
        level: matchedDegree.level,
        domain: matchedDegree.domain,
        fullName: matchedDegree.fullName,
        abbreviation: matchedDegree.abbreviation,
        specialization: specValue,
        duration: matchedDegree.duration || 'N/A',
        batches: newBatches,
        status: 'Active'
      };

      setFormData(prev => ({
        ...prev,
        departments: [...(prev.departments || []), newDept]
      }));
      toast.success('Degree added successfully');
    }

    resetModal();
  };

  const handleEditDeptGroup = (group) => {
    if (!group.batches || group.batches.length === 0) return;

    setSelectedLevel(group.level || '');
    setSelectedDomain(group.domain || '');
    setSelectedDegreeGroup(degreeLabelFromSaved(group));
    if (Array.isArray(group.specialization)) {
      setSelectedSpecialization(group.specialization[0] || '');
      setSelectedSpecializations(group.specialization);
    } else {
      setSelectedSpecialization(group.specialization || '');
      setSelectedSpecializations(group.specialization ? [group.specialization] : []);
    }

    const parseTimeline = (startYearVal, endYearVal) => {
      let startM = '06';
      let startY = startYearVal ? String(startYearVal).split('-')[0] : '';
      let endM = '06';
      let endY = endYearVal ? String(endYearVal).split('-')[0] : '';
      return { startM, startY, endM, endY };
    };

    const batchesToLoad = group.batches.map(b => {
      const startYearVal = b.startYear ? String(b.startYear).split('-')[0] : '';
      const endYearVal = b.endYear ? String(b.endYear).split('-')[0] : '';
      const parsed = parseTimeline(startYearVal, endYearVal);
      const timeline = startYearVal && endYearVal ? `June ${startYearVal} - June ${endYearVal}` : '';

      return {
        startYear: startYearVal,
        endYear: endYearVal,
        batch: b.batch || '',
        subscriptionPlan: {
          plan: b.subscriptionPlan?.plan || 'Smaart Core',
          addons: {
            aiq: true,
            sq: b.subscriptionPlan?.addons?.sq ?? (b.subscriptionPlan?.plan === 'Smaart Standard' || b.subscriptionPlan?.plan === 'Smaart Complete'),
            piq: b.subscriptionPlan?.addons?.piq ?? (b.subscriptionPlan?.plan === 'Smaart Complete'),
            britishCouncil: b.subscriptionPlan?.addons?.britishCouncil || false,
            collegePlacement: b.subscriptionPlan?.addons?.collegePlacement || false
          }
        },
        validityDuration: b.validityDuration || '1 Year',
        validityTimeline: timeline,
        validityStartMonth: parsed.startM,
        validityStartYear: parsed.startY,
        validityEndMonth: parsed.endM,
        validityEndYear: parsed.endY,
        status: b.status || 'Active',
        expanded: false
      };
    });

    if (batchesToLoad.length > 0) {
      batchesToLoad[0].expanded = true;
    }

    setModalBatches(batchesToLoad);
    setEditingOriginalIndices(group.indices);
    setIsDeptModalOpen(true);
  };

  const handleViewDeptGroup = (group) => {
    handleEditDeptGroup(group);
  };

  const resetModal = () => {
    setIsDeptModalOpen(false);
    setSelectedLevel('');
    setSelectedDomain('');
    setSelectedDegreeGroup('');
    setSelectedSpecialization('');
    setSelectedSpecializations([]);
    setModalBatches([{ ...defaultBatch }]);
    setEditingOriginalIndices(null);
    setDuplicateWarning(null);
  };

  const handleDeleteDeptGroup = (indices) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.filter((_, i) => !indices.includes(i))
    }));
  };

  const groupedDepartments = React.useMemo(() => {
    if (!formData.departments) return [];
    return formData.departments.map((dept, idx) => {
      const degreeAbbr = dept.abbreviation || dept.fullName || 'Degree';
      const specStr = Array.isArray(dept.specialization)
        ? dept.specialization.filter(s => s && s !== 'General').join(', ')
        : (dept.specialization && dept.specialization !== 'General' ? dept.specialization : '');
      const spec = specStr ? ` in ${specStr}` : '';
      const friendlyName = `${degreeAbbr}${spec}`;
      
      return {
        degreeId: dept.degreeId,
        uniqueId: dept.uniqueId,
        level: dept.level,
        domain: dept.domain,
        fullName: dept.fullName,
        abbreviation: dept.abbreviation,
        specialization: dept.specialization,
        duration: dept.duration,
        status: dept.status,
        
        degree: friendlyName,
        referenceName: friendlyName,
        batches: dept.batches || [],
        indices: [idx]
      };
    });
  }, [formData.departments]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);

    try {
      // Extract URL fields to avoid them getting corrupted by sanitizeObject
      const rawPayload = {
        collegeName: formData.institutionName,
        email: formData.institutionEmail,
        contactNumber: formData.contactNumber,
        institutionType: formData.institutionType,
        governanceType: formData.institutionType?.includes('University') ? undefined : formData.governanceType,
        address: {
          street: formData.addressLine,
          city: formData.city,
          district: formData.district,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country
        },
        onboardingDate: formData.dateOfOnboarding || undefined,
        isTemporary: isTemporary,

        // Conditionals
        affiliatedUniversity: formData.affiliatedUniversity,
        universityType: (formData.institutionType?.includes('University')) ? formData.institutionType.split(' ')[0] : undefined,
        instituteChain: formData.instituteChain || null,

        coordinatorInfo: {
          name: formData.coordinatorName,
          designation: formData.coordinatorDesignation,
          email: formData.coordinatorEmail,
          mobile: formData.coordinatorMobile
        },

        // Admin (always sent, password optional on edit)
        collegeAdmin: {
          name: formData.adminName,
          email: formData.adminEmail,
          mobile: formData.adminMobile,
          password: formData.adminPassword || undefined,
          role: 'college_admin'
        },

        registrationNumber: formData.registrationNumber,
        accreditationStatus: formData.accreditationStatus,
        gstNumber: formData.gstNumber,
        panNumber: formData.panNumber,

        maxFaculty: formData.maxFacultyAccounts ? Number(formData.maxFacultyAccounts) : undefined,
        maxStudents: formData.maxStudentAccounts ? Number(formData.maxStudentAccounts) : undefined,

        validityStartDate: formData.validityStartDate || undefined,
        validityEndDate: formData.validityEndDate || undefined,
        validityDuration: formData.validityDuration,
        status: formData.status,
        departments: (formData.departments || []).map(dept => {
          let degId = dept.degreeId;
          if (!degId || typeof degId !== 'string' || degId.length !== 24) {
            const matched = degrees.find(d => 
              d.level === dept.level && 
              d.domain === dept.domain && 
              (d.fullName === dept.fullName || d.abbreviation === dept.abbreviation) && 
              (Array.isArray(dept.specialization)
                ? dept.specialization.some(s => (d.specialization || 'General').toLowerCase().trim() === (s || 'General').toLowerCase().trim())
                : (d.specialization || 'General').toLowerCase().trim() === (dept.specialization || 'General').toLowerCase().trim())
            ) || degrees.find(d => 
              d.level === dept.level && 
              d.domain === dept.domain && 
              (d.fullName === dept.fullName || d.abbreviation === dept.abbreviation)
            );
            if (matched) {
              degId = matched._id;
            }
          }
          return {
            ...dept,
            degreeId: degId
          };
        }).filter(dept => dept.degreeId && dept.degreeId.length === 24),
        subscriptionPlan: formData.departments?.[0]?.batches?.[0]?.subscriptionPlan || formData.subscriptionPlan
      };

      const payload = sanitizeObject(rawPayload);

      // Explicitly append un-sanitized URL fields
      payload.website = formData.institutionWebsite;
      payload.logo = formData.logo;
      payload.mouDocument = formData.mouDocument;
      payload.universityActProof = formData.universityActProof;
      payload.ndaDocument = formData.ndaDocument;
      payload.chairmanVideo = formData.chairmanVideo;

      let response;
      if (id) {
        response = await api.put(`/colleges/${id}`, payload);
      } else {
        response = await api.post('/colleges', payload);
      }

      if (response.data.success) {
        toast.success(id ? 'Institution updated successfully' : 'Institution onboarded successfully');
        navigate('/colleges');
      }
    } catch (error) {
      console.error('Submission error:', error);
      if (error.response?.status === 404) {
        toast.error('Institute not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to save this institute');
      } else if (error.response?.status === 500) {
        toast.error('Server error while saving institute. Please try again later');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network');
      } else {
        toast.error(error.response?.data?.message || 'Failed to save institution');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to render file input with preview state
  const FileUploadField = ({ label, name, value, accept, required }) => {
    const isVideo = accept && accept.includes('video');
    return (
      <div className="bg-slate-50/50 dark:bg-[#001E3D]/30 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 hover:shadow-md transition-all flex flex-col justify-between space-y-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
              {isVideo ? <Video className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <label className="block text-sm font-bold text-gray-900 dark:text-white truncate">
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">
                {isVideo ? 'MP4 Video Format' : 'PDF or Image Proof'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="relative">
            <input
              type="file"
              id={name}
              accept={accept}
              onChange={(e) => handleFileUpload(e, name)}
              className="hidden"
              disabled={uploading}
            />
            <label
              htmlFor={name}
              className={`flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-750 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Upload size={14} className="mr-2 text-gray-500" />
              <span className="text-xs text-gray-600 dark:text-gray-300 font-semibold">
                {uploading ? 'Uploading...' : value ? 'Change File' : 'Upload File'}
              </span>
            </label>
          </div>

          {value ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-900/30">
                <CheckCircle size={12} className="mr-1" />
                Done
              </span>
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 underline"
              >
                View
              </a>
            </div>
          ) : (
            <span className="text-[11px] font-medium text-gray-400 italic">No file uploaded</span>
          )}
        </div>
      </div>
    );
  };

  const orderedLevels = [
    'Undergraduate (UG)',
    'Postgraduate (PG)',
    'Doctoral (PhD / Research)',
    'Professional / Integrated',
    'Diploma'
  ];
  const uniqueLevels = [...new Set(degrees.map(d => d.level))].sort((a, b) => {
    const idxA = orderedLevels.indexOf(a);
    const idxB = orderedLevels.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  // All three lists come from `levelPool` — the uploaded UG catalog when the
  // level is UG, the legacy master list otherwise. `withCurrent` re-adds the
  // value already saved on the college so editing an older registration never
  // opens on an empty or blank field.
  const domains = selectedLevel
    ? withCurrent([...new Set(levelPool.map(d => d.domain).filter(Boolean))].sort(), selectedDomain)
    : [];

  const degreeGroups = (selectedLevel && (isUgLevel || selectedDomain))
    ? withCurrent(
        [...new Set(degreeScope.map(degreeLabelOf).filter(Boolean))].sort(),
        selectedDegreeGroup
      )
    : [];

  const specializationOptions = (selectedLevel && selectedDegreeGroup && (isUgLevel || selectedDomain))
    ? withCurrent(
        [...new Set(
          specScope
            .filter(d => degreeMatches(d, selectedDegreeGroup))
            .map(d => d.specialization || 'General')
        )].sort(),
        selectedLevel === 'Postgraduate (PG)' ? selectedSpecializations : selectedSpecialization
      )
    : [];

  // The PG checkbox list still reads objects off this, so keep the same shape.
  const specializations = specializationOptions.map(specialization => ({ specialization }));

  const getEarliestStartYear = () => {
    if (!formData.departments || formData.departments.length === 0) return null;
    let minYear = null;
    formData.departments.forEach(dept => {
      const year = parseInt(dept.startYear);
      if (!isNaN(year)) {
        if (minYear === null || year < minYear) {
          minYear = year;
        }
      }
    });
    return minYear;
  };

  const calculatedStartYear = getEarliestStartYear() || new Date().getFullYear();
  const calculatedEndYear = calculatedStartYear + parseInt(formData.validityDuration || 1);

  return (
    <div className={`max-w-7xl mx-auto pb-20 ${degreesOnly ? 'pt-6' : 'space-y-6'}`}>
      {/* Header — title on the left, live progress on the right, with a slim
          progress rail along the bottom edge. Replaces the old stacked pair of
          cards (a lone progress card floating above a bare header). */}
      <div className="bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/35 shadow-sm rounded-2xl overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            {!degreesOnly && (
              <button
                onClick={() => navigate(-1)}
                type="button"
                className="p-2.5 bg-white dark:bg-[#002147] hover:bg-gray-50 dark:hover:bg-[#1a3884]/30 rounded-xl border border-gray-200 dark:border-[#1a3884]/40 text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 active:scale-95 transition-all shadow-sm shrink-0 cursor-pointer"
                title="Back"
                aria-label="Go back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl shrink-0">
              <Building2 className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
            </div>
            <div className="text-left min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-[#1a3884] dark:text-[#4f7dff] leading-tight truncate">
                {degreesOnly ? 'Manage Degrees & Batches' : (isTemporary ? 'Onboard Temporary Institution' : (id ? 'Edit Institution' : 'Onboard New Institution'))}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {degreesOnly ? formData.institutionName : 'Complete the sections below to onboard an Institute or University.'}
              </p>
            </div>
          </div>

          {!degreesOnly && !isTemporary && (
            <div className="text-left sm:text-right shrink-0">
              <span className="text-sm font-bold text-[#1a3884] dark:text-indigo-300">{calculateProgress()}%</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">complete</span>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                Section {Math.ceil(calculateProgress() / 11.11)} of 9
              </p>
            </div>
          )}
        </div>

        {!degreesOnly && !isTemporary && (
          <div className="h-1.5 bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full bg-[#1a3884] dark:bg-[#4f7dff] transition-all duration-300"
              style={{ width: `${calculateProgress()}%` }}
            ></div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {!degreesOnly && !id && (
            <div className="bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/35 shadow-sm px-5 py-4 rounded-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <p className="text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Onboarding Type</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                        {isTemporary
                          ? 'Quick entry with a temporary ID — convert to permanent later.'
                          : 'Full onboarding with a standard institution ID.'}
                    </p>
                </div>
                <div className="inline-flex self-start sm:self-auto rounded-xl border border-gray-200 dark:border-[#1a3884]/40 bg-gray-100 dark:bg-[#00152e] p-1 gap-1">
                    <button
                        type="button"
                        onClick={() => setIsTemporary(false)}
                        className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-150 active:scale-95 cursor-pointer select-none ${!isTemporary ? 'bg-[#1a3884] text-white shadow-sm ring-1 ring-black/5 dark:ring-white/10' : 'text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white'}`}
                    >
                        Permanent Institute
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsTemporary(true)}
                        className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-150 active:scale-95 cursor-pointer select-none ${isTemporary ? 'bg-[#1a3884] text-white shadow-sm ring-1 ring-black/5 dark:ring-white/10' : 'text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white'}`}
                    >
                        Temporary Institute
                    </button>
                </div>
            </div>
        )}

        {!degreesOnly && (
          <Card title={<SectionTitle step={1} title="Institution Identity" subtitle="Name, classification and contact details of the institution" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Institution Name" name="institutionName" value={formData.institutionName} onChange={handleChange} required maxLength={100} icon={Building2} placeholder="Enter institution name" />
              <Input label="Institution Code" name="institutionCode" value={formData.institutionCode} disabled className="bg-gray-50" placeholder="Auto-generated" />

              <Select label="Institution Type" name="institutionType" value={formData.institutionType} onChange={handleChange} options={institutionTypeOptions} required />
              {formData.institutionType && !formData.institutionType.includes('University') && (
                <Select
                  label="Type of Institution (Governance)"
                  name="governanceType"
                  value={formData.governanceType}
                  onChange={handleChange}
                  options={governanceOptions}
                  required
                />
              )}

              <Select
                label="Institute Chain"
                name="instituteChain"
                value={formData.instituteChain}
                onChange={handleChange}
                placeholder="Select Institute Chain (Optional)"
                options={chains.map(c => ({ value: c._id, label: c.chainName }))}
              />

              {(formData.institutionType?.toLowerCase().includes('college') || formData.institutionType === 'College') && (
                <Input
                  label="Affiliated University"
                  name="affiliatedUniversity"
                  value={formData.affiliatedUniversity}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Anna University"
                />
              )}

              <Input label="Institution Email" name="institutionEmail" value={formData.institutionEmail} onChange={handleChange} type="email" required icon={Mail} error={errors.institutionEmail} placeholder="example@institution.edu" />
              <Input label="Institution Website" name="institutionWebsite" value={formData.institutionWebsite} onChange={handleChange} required type="url" icon={Globe} placeholder="https://www.example.edu" />
              <Input label="Contact Number" name="contactNumber" value={formData.contactNumber} onChange={handleChange} type="tel" maxLength={10} required icon={Phone} error={errors.contactNumber} placeholder="10-digit number" />

              <Select label="Country" name="country" value={formData.country} onChange={handleChange} options={countryOptions} required />
              <Select
                label="State"
                name="state"
                value={formData.state}
                onChange={(e) => {
                  // Changing state invalidates the district, city and pincode below it
                  setFormData(prev => ({ ...prev, state: e.target.value, district: '', city: '', pincode: '' }));
                  setCitySearch('');
                }}
                options={stateOptions}
                required
              />
              <Select
                label="District"
                name="district"
                value={formData.district || ''}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, district: e.target.value, city: '', pincode: '' }));
                  setCitySearch('');
                }}
                options={districtOptions}
                disabled={!formData.state}
                required
              />

              {/* City – Searchable Dropdown (India only) */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <div
                  className="w-full flex items-center border-2 border-gray-200 rounded-lg px-3 py-2 cursor-pointer bg-white hover:border-indigo-400 transition-colors"
                  onClick={() => {
                    if (formData.district) setCityDropdownOpen(v => !v);
                    else toast.error(formData.state ? 'Please select a District first' : 'Please select a State first');
                  }}
                >
                  <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className={`text-sm flex-1 \${formData.city ? 'text-gray-900' : 'text-gray-400'}`}>
                    {sanitizeInput(formData.city) || (formData.district ? 'Search and select city / town...' : 'Select District first')}
                  </span>
                  <span className="text-gray-400 text-xs">{cityDropdownOpen ? '▲' : '▼'}</span>
                </div>
                {cityDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-indigo-200 rounded-xl shadow-xl">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        autoFocus
                        value={citySearch}
                        onChange={e => setCitySearch(e.target.value)}
                        placeholder="Type to search cities..."
                        className="w-full text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    <ul className="max-h-48 overflow-y-auto py-1">
                      {filteredCities.length === 0 ? (
                        <li className="text-xs text-gray-400 px-4 py-2">No cities found</li>
                      ) : filteredCities.map(city => (
                        <li
                          key={city}
                          className={`text-sm px-4 py-2 cursor-pointer hover:bg-indigo-50 transition-colors \${formData.city === city ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-700'
                            }`}
                          onClick={() => {
                            const pin = getPincodeForCity(formData.state, formData.district, city);
                            setFormData(prev => ({ ...prev, city, pincode: pin || prev.pincode }));
                            setCitySearch('');
                            setCityDropdownOpen(false);
                            setErrors(prev => ({ ...prev, city: '' }));
                          }}
                        >
                          {sanitizeInput(city)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Address Fields */}
              <div className="md:col-span-2">
                <Input label="Address Line" name="addressLine" value={formData.addressLine} onChange={handleChange} required maxLength={255} icon={MapPin} placeholder="Street address, building name, etc." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\\D/g, '').slice(0, 6);
                    handleChange({ target: { name: 'pincode', value: val } });
                  }}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit pincode"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>

              <Input label="Date of Onboarding" name="dateOfOnboarding" value={formData.dateOfOnboarding} onChange={handleChange} type="date" required icon={Calendar} />

              {/* Institution Logo - Moved to end */}
              <div className="md:col-span-2">
                <FileUploadField label="Institution Logo" name="logo" value={formData.logo} accept="image/*" required />
              </div>
            </div>
          </Card>
        )}

        {/* SECTION: Degree Information */}
        {!isTemporary && (
          <>
        <Card title={<SectionTitle step={degreesOnly ? null : 2} title="Degree Information" subtitle="Degrees, specialisations and intake batches offered" />}>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add and manage degrees for this institution.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsDeptModalOpen(true)}
                className="flex items-center gap-1.5"
              >
                <Plus size={16} /> Add Degree
              </Button>
            </div>

            {groupedDepartments && groupedDepartments.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {groupedDepartments.map((group, groupIdx) => (
                  <div
                    key={groupIdx}
                    className="relative flex items-start justify-between p-4 bg-white dark:bg-[#001E3D]/50 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-4 w-full">
                      <div className="mt-1 p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                        <BookOpen size={20} />
                      </div>
                      <div className="space-y-3 w-full pr-8">
                        <div className="flex flex-col">
                          <h4 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                            {group.referenceName}
                          </h4>
                          <span className="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-100/50 w-max">
                            {group.degree}
                          </span>
                        </div>

                        {/* Batches List */}
                        <div className="flex flex-col gap-2 mt-2 w-full">
                          {group.batches.map((batch, bIdx) => {
                            const addons = batch.subscriptionPlan?.addons || {};
                            const activeAddonsList = [];
                            if (batch.subscriptionPlan?.plan === 'Smaart Core' && addons.sq) activeAddonsList.push('SQ');
                            if ((batch.subscriptionPlan?.plan === 'Smaart Core' || batch.subscriptionPlan?.plan === 'Smaart Standard') && addons.piq) activeAddonsList.push('PIQ');
                            if (addons.britishCouncil) activeAddonsList.push('British Council');
                            if (addons.collegePlacement) activeAddonsList.push('Placement');

                            return (
                              <div key={bIdx} className="flex flex-wrap items-center gap-2 md:gap-3 bg-gray-50/80 dark:bg-[#001736] p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                  Batch: {batch.batch}
                                </span>
                                <span className="text-xs text-gray-300 dark:text-gray-600 font-medium hidden sm:inline">•</span>
                                <span className="text-[11px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider">
                                  {batch.startYear && batch.endYear && !isNaN(parseInt(batch.startYear)) && !isNaN(parseInt(batch.endYear)) && parseInt(batch.endYear) > parseInt(batch.startYear)
                                    ? `${parseInt(batch.endYear) - parseInt(batch.startYear)} Year${parseInt(batch.endYear) - parseInt(batch.startYear) !== 1 ? 's' : ''} Program`
                                    : `${batch.startYear} - ${batch.endYear}`}
                                </span>
                                <span className="text-xs text-gray-300 dark:text-gray-600 font-medium hidden sm:inline">•</span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 uppercase tracking-wider">
                                  {batch.subscriptionPlan?.plan || 'Smaart Core'}
                                  {activeAddonsList.length > 0 && (
                                    <span className="text-[9px] font-semibold text-purple-600 dark:text-purple-400 lowercase">
                                      (+{activeAddonsList.join(', ')})
                                    </span>
                                  )}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wider ${batch.status === 'Active' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/50' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'}`}>
                                  {batch.status || 'Draft'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 flex flex-col gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleViewDeptGroup(group)}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 rounded-lg transition-colors flex-shrink-0"
                        title="View Degree"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditDeptGroup(group)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors flex-shrink-0"
                        title="Edit Degree"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteDeptGroup(group.indices)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex-shrink-0"
                        title="Delete Degree"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50/50 dark:bg-[#001f40]/5 rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                <p className="text-sm text-gray-500 dark:text-gray-400">No degrees added yet. Click "Add Degree" to start.</p>
              </div>
            )}
          </div>
        </Card>



          </>
        )}

        {/* SECTION 4: Primary Coordinator */}
        {!degreesOnly && !isTemporary && (
          <>
            <Card title={<SectionTitle step={3} title="Primary Coordinator" subtitle="Day-to-day point of contact at the institution" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Coordinator Name" name="coordinatorName" value={formData.coordinatorName} onChange={handleChange} required maxLength={100} icon={User} placeholder="Full name" />
                <Input label="Designation" name="coordinatorDesignation" value={formData.coordinatorDesignation} onChange={handleChange} required placeholder="e.g., Dean, Principal" />
                <Input label="Coordinator Email" name="coordinatorEmail" value={formData.coordinatorEmail} onChange={handleChange} required type="email" icon={Mail} error={errors.coordinatorEmail} placeholder="coordinator@example.edu" />
                <Input label="Coordinator Mobile" name="coordinatorMobile" value={formData.coordinatorMobile} onChange={handleChange} required type="tel" maxLength={10} icon={Phone} error={errors.coordinatorMobile} placeholder="10-digit number" />
              </div>
            </Card>

            {/* SECTION 5: About College Admin */}
            <Card title={<SectionTitle step={4} title="College Admin Account" subtitle="Login credentials for the institution's administrator" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Admin Name" name="adminName" value={formData.adminName} onChange={handleChange} required maxLength={100} icon={User} placeholder="Full name" autoComplete="off" />
                <Input label="Admin Email (Login)" name="adminEmail" value={formData.adminEmail} onChange={handleChange} required type="email" icon={Mail} error={errors.adminEmail} placeholder="admin@example.edu" autoComplete="off" />
                <Input label="Admin Mobile" name="adminMobile" value={formData.adminMobile} onChange={handleChange} required type="tel" maxLength={10} icon={Phone} error={errors.adminMobile} placeholder="10-digit number" autoComplete="off" />
                <Input label="Role" name="adminRole" value="Institute Admin" disabled className="bg-gray-50" />

                <div>
                  <Input
                    label="Admin Password"
                    name="adminPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.adminPassword}
                    onChange={handleChange}
                    required={!id}
                    placeholder={id ? "Enter new password (optional)" : "Enter password"}
                    icon={showPassword ? EyeOff : Eye}
                    onIconClick={() => setShowPassword(!showPassword)}
                    autoComplete="new-password"
                  />
                  {/* Strength Meter */}
                  {formData.adminPassword && (
                    <div className="mt-2">
                      <div className="flex gap-1 h-1">
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            className={`flex-1 rounded-full ${i < getPasswordStrength(formData.adminPassword)
                              ? (getPasswordStrength(formData.adminPassword) < 3 ? 'bg-red-400' : 'bg-green-500')
                              : 'bg-gray-200'
                              }`}
                          />
                        ))}
                      </div>
                      <p className="text-[10px] mt-1 text-gray-500 flex justify-between">
                        <span>Strength: {['Weak', 'Fair', 'Good', 'Strong'][getPasswordStrength(formData.adminPassword) - 1] || 'Weak'}</span>
                        <span className="text-gray-400">Req: A-Z, 0-9, @#$</span>
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Input
                    label="Confirm Password"
                    name="adminConfirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.adminConfirmPassword}
                    onChange={handleChange}
                    required={!!formData.adminPassword}
                    placeholder={id ? "Re-enter new password" : "Re-enter password"}
                    icon={showConfirmPassword ? EyeOff : Eye}
                    onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    autoComplete="new-password"
                  />
                  {formData.adminConfirmPassword && (
                    <p className={`text-xs mt-1 ${formData.adminPassword === formData.adminConfirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                      {formData.adminPassword === formData.adminConfirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
              </div>
            </Card>
            {/* SECTION 6: Compliance */}
            <Card title={<SectionTitle step={5} title="Compliance" subtitle="PAN, GST and statutory identifiers" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Institution Registration Number" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} required maxLength={50} icon={Shield} placeholder="Enter registration number" />
                <Select label="Accreditation Status" name="accreditationStatus" value={formData.accreditationStatus} onChange={handleChange} options={accreditationOptions} required />
                <Input label="GST Number" name="gstNumber" value={formData.gstNumber} onChange={handleChange} maxLength={15} error={errors.gstNumber} placeholder="Optional - 15 characters" />
                <Input label="PAN / Institutional Tax ID" name="panNumber" value={formData.panNumber} onChange={handleChange} maxLength={10} error={errors.panNumber} placeholder="Optional - 10 characters" />
              </div>
            </Card>

            {/* SECTION 7: Mandatory Documents */}
            <Card title={<SectionTitle step={6} title="Mandatory Documents" subtitle="Logo and registration proofs — image files only" />}>
              <div className="grid grid-cols-1 gap-6">
                <FileUploadField label="MoU / Authorization Letter" name="mouDocument" value={formData.mouDocument} accept="image/*" required />
                <FileUploadField label="Registration / University Act Proof" name="universityActProof" value={formData.universityActProof} accept="image/*" required />
                <FileUploadField label="NDA / Service Agreement" name="ndaDocument" value={formData.ndaDocument} accept="image/*" />
                <FileUploadField label="Principal / Chairman Introduction Video" name="chairmanVideo" value={formData.chairmanVideo} accept="video/mp4" required />
              </div>
            </Card>

            {/* SECTION 9: Status & Validity */}
            <Card title={<SectionTitle step={7} title="Status & Validity" subtitle="Subscription window and activation state" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Validity – Start Date" name="validityStartDate" value={formData.validityStartDate} onChange={handleChange} type="date" required />
                <Select label="Validity Duration" name="validityDuration" value={formData.validityDuration} onChange={handleChange} options={validityDurationOptions} required />
                <div>
                  <Input
                    label="Validity – End Date"
                    name="validityEndDate"
                    value={formData.validityEndDate}
                    onChange={handleChange}
                    type="date"
                    disabled
                    className="bg-gray-50 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated: {formData.validityDuration} year(s) from start date</p>
                </div>
                <Select label="Institution Status" name="status" value={formData.status} onChange={handleChange} options={statusOptions} required />

                <div className="md:col-span-2 grid grid-cols-3 gap-4 text-xs text-gray-500 mt-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <span className="font-semibold block">Created By:</span>
                    Auto-captured (System)
                  </div>
                  <div>
                    <span className="font-semibold block">Last Updated By:</span>
                    Auto-captured (System)
                  </div>
                  <div>
                    <span className="font-semibold block">Timestamp:</span>
                    {new Date().toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}




        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-4">
          {!degreesOnly && (
            <Button variant="outline" type="button" onClick={() => navigate('/colleges')} className="w-full sm:w-auto">Cancel</Button>
          )}
          <Button variant="primary" type="submit" disabled={loading || uploading}>
            {loading ? 'Saving...' : (degreesOnly ? 'Save Changes' : (id ? 'Update Institution' : 'Complete Onboarding'))}
          </Button>
        </div>

      </form>

      <Modal
        isOpen={isDeptModalOpen}
        onClose={resetModal}
        title={editingOriginalIndices !== null ? "Edit Degree" : "Add Degree"}
        size="3xl"
      >
        <form onSubmit={handleAddDept} className="w-full space-y-6">
          <div className="w-full space-y-10">
            {/* Degree Information */}
            <div className="space-y-6">
              <div className="border-b border-gray-100 dark:border-gray-800 pb-3">
                <h3 className="text-lg font-extrabold text-gray-900 dark:text-white">Degree Information</h3>
                <p className="text-sm text-gray-500 mt-1">Provide the fundamental details of the degree batch.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SearchableDropdown
                  label="Degree Level"
                  required
                  value={selectedLevel}
                  onChange={(val) => {
                    setSelectedLevel(val);
                    setSelectedDomain('');
                    setSelectedDegreeGroup('');
                    setSelectedSpecialization('');
                    setSelectedSpecializations([]);
                    setDuplicateWarning(null);
                  }}
                  options={uniqueLevels}
                  placeholder="Select or type Level..."
                />

                {/* UG comes straight from the uploaded catalog, which is a flat
                    Degree -> Specialisation list with no domain column. Asking
                    for a domain first only got in the way, so it is hidden here
                    and derived from the chosen degree when saving. The other
                    levels still have properly populated domains, so they keep
                    the Level -> Domain -> Degree flow. */}
                {!isUgLevel && (
                  <SearchableDropdown
                    label="Domain"
                    required
                    value={selectedDomain}
                    disabled={!selectedLevel}
                    onChange={(val) => {
                      setSelectedDomain(val);
                      setSelectedDegreeGroup('');
                      setSelectedSpecialization('');
                      setSelectedSpecializations([]);
                      setDuplicateWarning(null);
                    }}
                    options={domains}
                    placeholder="Select or type Domain..."
                  />
                )}

                <SearchableDropdown
                  label="Degree"
                  required
                  value={selectedDegreeGroup}
                  disabled={isUgLevel ? !selectedLevel : !selectedDomain}
                  onChange={(val) => {
                    setSelectedDegreeGroup(val);
                    setSelectedSpecialization('');
                    setSelectedSpecializations([]);
                    setDuplicateWarning(null);
                    // Domain is not asked for on UG, so carry it over from the
                    // catalog record — CollegeDegree requires one to save.
                    if (isUgLevel) {
                      const match = levelPool.find(d => degreeMatches(d, val));
                      setSelectedDomain(match?.domain || '');
                    }
                  }}
                  options={degreeGroups}
                  placeholder="Select or type Degree..."
                />

                {selectedLevel === 'Postgraduate (PG)' ? (
                  <div className="flex flex-col gap-1.5 w-full md:col-span-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Specialisation(s) <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional - Select multiple)</span>
                    </label>
                    <div className={`space-y-2 max-h-48 overflow-y-auto border-2 rounded-xl p-3 bg-white dark:bg-[#001736] ${!selectedDegreeGroup ? 'border-gray-150 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed' : 'border-gray-200 dark:border-gray-700'}`}>
                      {specializations.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No specializations available</p>
                      ) : (
                        [...new Set(specializations.map(s => s.specialization || 'General'))].map(specName => {
                          const isChecked = selectedSpecializations.includes(specName);
                          return (
                            <label key={specName} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-850 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={!selectedDegreeGroup}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSpecializations([...selectedSpecializations, specName]);
                                  } else {
                                    setSelectedSpecializations(selectedSpecializations.filter(s => s !== specName));
                                  }
                                }}
                                className="rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] h-4 w-4 cursor-pointer"
                              />
                              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 cursor-pointer">
                                {specName}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  <SearchableDropdown
                    label="Specialisation(s)"
                    required
                    value={selectedSpecialization}
                    disabled={!selectedDegreeGroup}
                    onChange={(val) => {
                      setSelectedSpecialization(val);
                      setDuplicateWarning(null);
                    }}
                    options={specializations.map(spec => spec.specialization)}
                    placeholder="Select or type Specialisation..."
                  />
                )}
              </div>

              {/* Duplicate Degree Warning Banner */}
              {duplicateWarning && (
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-4 flex items-start gap-3 mt-4">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">Degree Already Exists</h4>
                    <p className="text-xs text-amber-700 dark:text-amber-400/80 leading-relaxed">
                      "{duplicateWarning.name}" is already added to this institution. You cannot add a duplicate degree configuration — but you can add a new batch to the existing record instead.
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          const groupToEdit = duplicateWarning.group;
                          resetModal();
                          handleEditDeptGroup(groupToEdit);
                        }}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                      >
                        Add Batch to Existing Degree
                      </button>
                      <button
                        type="button"
                        onClick={() => setDuplicateWarning(null)}
                        className="text-xs font-bold text-amber-700 dark:text-amber-400 hover:underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Batches Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-gray-100 dark:border-gray-800 pb-3">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 dark:text-white">Batches & Intakes</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage intake batches, plans, and validity for this degree.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const collapsedBatches = modalBatches.map(b => ({ ...b, expanded: false }));
                    setModalBatches([...collapsedBatches, { ...defaultBatch }]);
                  }}
                  className="flex items-center gap-1.5"
                >
                  <Plus size={16} /> Add Batch
                </Button>
              </div>

              <div className="space-y-6">
                {modalBatches.map((batch, idx) => (
                  <div key={idx} className="bg-white dark:bg-[#001736] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm">
                    {/* Batch Header */}
                    <div className="p-4 bg-gray-50 dark:bg-[#001E3D] border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs font-bold">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <h4 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                          Batch: {batch.batch || 'New'}
                          {batch.startYear && batch.endYear && !isNaN(parseInt(batch.startYear)) && !isNaN(parseInt(batch.endYear)) && parseInt(batch.endYear) > parseInt(batch.startYear) && (
                            <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-blue-200 dark:border-blue-800/50">
                              {parseInt(batch.endYear) - parseInt(batch.startYear)} Year{parseInt(batch.endYear) - parseInt(batch.startYear) !== 1 ? 's' : ''} Program
                            </span>
                          )}
                        </h4>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center cursor-pointer mr-2" title="Toggle Status">
                          <div className="relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={batch.status === 'Active'}
                              onChange={(e) => updateBatch(idx, 'status', e.target.checked ? 'Active' : 'Draft')}
                            />
                            <div className={`block w-10 h-6 rounded-full transition-colors ${batch.status === 'Active' ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${batch.status === 'Active' ? 'transform translate-x-4' : ''}`}></div>
                          </div>
                          <span className={`ml-2 text-xs font-bold ${batch.status === 'Active' ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'}`}>
                            {batch.status}
                          </span>
                        </label>
                        {modalBatches.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setModalBatches(modalBatches.filter((_, i) => i !== idx))}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => updateBatch(idx, 'expanded', !batch.expanded)}
                          className="text-gray-500 hover:text-indigo-600 font-medium text-sm px-2 flex items-center gap-1"
                        >
                          {batch.expanded ? 'Collapse' : 'Expand'}
                        </button>
                      </div>
                    </div>

                    {/* Batch Form */}
                    {batch.expanded && (
                      <div className="p-5 space-y-8">
                        {/* Batch Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Start Year <span className="text-red-500">*</span>
                            </label>
                            <Select
                              placeholder="Select Start Year"
                              options={yearOptions}
                              value={batch.startYear ? String(batch.startYear).split('-')[0] : ''}
                              onChange={(e) => updateBatch(idx, 'startYear', e.target.value)}
                              required
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              End Year <span className="text-red-500">*</span>
                            </label>
                            <Select
                              placeholder="Select End Year"
                              options={yearOptions}
                              value={batch.endYear ? String(batch.endYear).split('-')[0] : ''}
                              onChange={(e) => updateBatch(idx, 'endYear', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Input
                              label="Batch Name"
                              required
                              value={batch.batch}
                              disabled
                              className="bg-gray-50 cursor-not-allowed font-semibold text-gray-700"
                              placeholder="Auto-calculated (e.g. 2026-2029)"
                            />
                          </div>
                          <div className="flex flex-col gap-1 col-span-1 md:col-span-3">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              SMAART Program Validity Period
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <Select
                                placeholder="Start Month"
                                options={monthOptions}
                                value={batch.validityStartMonth || '06'}
                                onChange={(e) => updateBatch(idx, 'validityStartMonth', e.target.value)}
                              />
                              <Select
                                placeholder="Start Year"
                                options={yearOptions}
                                value={batch.validityStartYear || (batch.startYear ? String(batch.startYear).split('-')[0] : '')}
                                onChange={(e) => updateBatch(idx, 'validityStartYear', e.target.value)}
                              />
                              <Select
                                placeholder="End Month"
                                options={monthOptions}
                                value={batch.validityEndMonth || '06'}
                                onChange={(e) => updateBatch(idx, 'validityEndMonth', e.target.value)}
                              />
                              <Select
                                placeholder="End Year"
                                options={yearOptions}
                                value={batch.validityEndYear || (batch.endYear ? String(batch.endYear).split('-')[0] : '')}
                                onChange={(e) => updateBatch(idx, 'validityEndYear', e.target.value)}
                              />
                            </div>
                            {batch.validityTimeline && (
                              <p className="text-xs text-[#1a3884] dark:text-indigo-400 font-bold mt-1">
                                Preview: {batch.validityTimeline}
                              </p>
                            )}
                          </div>
                        </div>



                        {/* Subscription Plan & Features Section */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-blue-50 dark:bg-blue-950/60 text-[#1a3884] dark:text-[#4f7dff] rounded-lg">
                                <BookOpen size={16} />
                              </div>
                              <div>
                                <h5 className="text-sm font-bold text-gray-900 dark:text-white">Batch Subscription Plan</h5>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">Select tier and customize optional modular add-ons for this batch.</p>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-[#1a3884]/10 dark:bg-[#4f7dff]/20 text-[#1a3884] dark:text-[#4f7dff] border border-[#1a3884]/20 w-fit">
                              {batch.subscriptionPlan?.plan || 'Smaart Core'}
                            </span>
                          </div>

                          {/* Plan Selection Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { id: 'Smaart Core', name: 'Smaart Core', desc: 'Core curriculum with AIQ Quotient' },
                              { id: 'Smaart Standard', name: 'Smaart Standard', desc: 'Standard path with SQ included' },
                              { id: 'Smaart Complete', name: 'Smaart Complete', desc: 'Full access with PIQ & SQ included' }
                            ].map(plan => {
                              const isSelected = (batch.subscriptionPlan?.plan || 'Smaart Core') === plan.id;
                              return (
                                <button
                                  key={plan.id}
                                  type="button"
                                  onClick={() => updateBatchPlan(idx, plan.id)}
                                  className={`relative p-3.5 rounded-xl border text-left transition-all duration-200 group overflow-hidden ${isSelected
                                    ? 'border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/50 dark:bg-blue-950/40 shadow-sm ring-2 ring-[#1a3884]/20 dark:ring-[#4f7dff]/30'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#001736] hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50/50'
                                    }`}
                                >
                                  <div className="flex justify-between items-start mb-1">
                                    <span className={`text-sm font-bold ${isSelected ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-900 dark:text-gray-100'}`}>
                                      {plan.name}
                                    </span>
                                    {isSelected ? (
                                      <span className="text-[#1a3884] dark:text-[#4f7dff] bg-blue-100 dark:bg-blue-900/60 p-0.5 rounded-full">
                                        <CheckCircle size={14} />
                                      </span>
                                    ) : (
                                      <span className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600 group-hover:border-blue-400"></span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium leading-snug">{plan.desc}</p>
                                </button>
                              );
                            })}
                          </div>

                          {/* Included Features Summary */}
                          <div className="bg-gray-50/80 dark:bg-[#001430]/70 border border-gray-150 dark:border-gray-800 rounded-xl p-3.5 space-y-2.5">
                            <span className="text-[11px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                              <Shield size={13} className="text-[#1a3884] dark:text-[#4f7dff]" /> Features Included for this Batch
                            </span>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {[
                                { name: 'Capacity', included: true, type: 'default' },
                                { name: 'Capability', included: true, type: 'default' },
                                { name: 'Leadership', included: true, type: 'default' },
                                { name: 'AIQ (Artificial Intelligence)', included: true, type: 'default' },
                                {
                                  name: 'SQ (Sustainability Quotient)',
                                  included: batch.subscriptionPlan?.plan === 'Smaart Standard' || batch.subscriptionPlan?.plan === 'Smaart Complete' || !!batch.subscriptionPlan?.addons?.sq,
                                  type: (batch.subscriptionPlan?.plan === 'Smaart Standard' || batch.subscriptionPlan?.plan === 'Smaart Complete') ? 'default' : (batch.subscriptionPlan?.addons?.sq ? 'addon' : 'none')
                                },
                                {
                                  name: 'PIQ (Personal Intelligence)',
                                  included: batch.subscriptionPlan?.plan === 'Smaart Complete' || !!batch.subscriptionPlan?.addons?.piq,
                                  type: batch.subscriptionPlan?.plan === 'Smaart Complete' ? 'default' : (batch.subscriptionPlan?.addons?.piq ? 'addon' : 'none')
                                },
                                {
                                  name: 'British Council',
                                  included: !!batch.subscriptionPlan?.addons?.britishCouncil,
                                  type: batch.subscriptionPlan?.addons?.britishCouncil ? 'addon' : 'none'
                                },
                                {
                                  name: 'College Placement',
                                  included: !!batch.subscriptionPlan?.addons?.collegePlacement,
                                  type: batch.subscriptionPlan?.addons?.collegePlacement ? 'addon' : 'none'
                                }
                              ].map((feat, fIdx) => (
                                <div
                                  key={fIdx}
                                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                                    feat.included
                                      ? 'bg-white dark:bg-[#001f40] border-blue-150 dark:border-blue-900/50 text-gray-800 dark:text-gray-100 font-medium shadow-2xs'
                                      : 'border-transparent text-gray-400 dark:text-gray-600 opacity-40'
                                  }`}
                                >
                                  <span className={feat.included ? 'text-emerald-500 font-bold' : 'text-gray-300'}>
                                    {feat.included ? '✓' : '•'}
                                  </span>
                                  <span className="truncate">{feat.name}</span>
                                  {feat.included && feat.type === 'addon' && (
                                    <span className="ml-auto text-[9px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-1 rounded">
                                      Add-on
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Available Add-ons */}
                          <div className="space-y-2">
                            <span className="text-[11px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                              <Plus size={13} className="text-[#1a3884] dark:text-[#4f7dff]" /> Available Modular Add-ons
                            </span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                              {batch.subscriptionPlan?.plan === 'Smaart Core' && (
                                <label className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                  batch.subscriptionPlan?.addons?.sq
                                    ? 'border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/30 dark:bg-blue-950/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#001736] hover:border-gray-300'
                                }`}>
                                  <div className="pr-3">
                                    <span className={`text-xs font-bold block ${batch.subscriptionPlan?.addons?.sq ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-800 dark:text-gray-200'}`}>
                                      SQ (Sustainability Quotient)
                                    </span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Unlock Sustainability Quotient module</span>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={!!batch.subscriptionPlan?.addons?.sq}
                                    onChange={(e) => updateBatchAddon(idx, 'sq', e.target.checked)}
                                    className="rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] h-4 w-4 cursor-pointer"
                                  />
                                </label>
                              )}

                              {(batch.subscriptionPlan?.plan === 'Smaart Core' || batch.subscriptionPlan?.plan === 'Smaart Standard') && (
                                <label className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                  batch.subscriptionPlan?.addons?.piq
                                    ? 'border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/30 dark:bg-blue-950/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#001736] hover:border-gray-300'
                                }`}>
                                  <div className="pr-3">
                                    <span className={`text-xs font-bold block ${batch.subscriptionPlan?.addons?.piq ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-800 dark:text-gray-200'}`}>
                                      PIQ (Personal Intelligent Quotient)
                                    </span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Unlock Personal Intelligent Quotient module</span>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={!!batch.subscriptionPlan?.addons?.piq}
                                    onChange={(e) => updateBatchAddon(idx, 'piq', e.target.checked)}
                                    className="rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] h-4 w-4 cursor-pointer"
                                  />
                                </label>
                              )}

                              <label className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                batch.subscriptionPlan?.addons?.britishCouncil
                                  ? 'border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/30 dark:bg-blue-950/20'
                                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#001736] hover:border-gray-300'
                              }`}>
                                <div className="pr-3">
                                  <span className={`text-xs font-bold block ${batch.subscriptionPlan?.addons?.britishCouncil ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-800 dark:text-gray-200'}`}>
                                    British Council
                                  </span>
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Add British Council Credentialing</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={!!batch.subscriptionPlan?.addons?.britishCouncil}
                                  onChange={(e) => updateBatchAddon(idx, 'britishCouncil', e.target.checked)}
                                  className="rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] h-4 w-4 cursor-pointer"
                                />
                              </label>

                              <label className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                batch.subscriptionPlan?.addons?.collegePlacement
                                  ? 'border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/30 dark:bg-blue-950/20'
                                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#001736] hover:border-gray-300'
                              }`}>
                                <div className="pr-3">
                                  <span className={`text-xs font-bold block ${batch.subscriptionPlan?.addons?.collegePlacement ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-800 dark:text-gray-200'}`}>
                                    College Placement
                                  </span>
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Unlock Post Opportunity, Partner & Job Fair</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={!!batch.subscriptionPlan?.addons?.collegePlacement}
                                  onChange={(e) => updateBatchAddon(idx, 'collegePlacement', e.target.checked)}
                                  className="rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] h-4 w-4 cursor-pointer"
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-200">
            <Button variant="outline" type="button" onClick={resetModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!selectedLevel || !selectedDomain || !selectedDegreeGroup || modalBatches.some(b => !b.startYear || !b.endYear)}>
              {editingOriginalIndices !== null ? "Save Changes" : "Add Degree & Batches"}
            </Button>
          </div>
        </form>
      </Modal>
    </div >
  );
};

export default OnboardCollege;
